/**
 * The post-call prescription flow (frontend §8 – §12).
 *
 * AI Draft → Doctor Review → Doctor Edit → Doctor Approve/Sign → Issued
 *
 * Every gate in that flow is enforced by the service; this screen's job is to
 * make the gates legible. Three things it never does, matching the spec:
 *
 *   - It never offers an AI medication suggestion. The medications editor is
 *     empty until the doctor types into it, on the AI path and the manual path
 *     alike, and it says so.
 *   - It never issues on approval. Signing and issuing are two deliberate
 *     actions, and the screen shows what was signed before offering the second.
 *   - It never blocks on the AI. If the draft failed, the same editor opens
 *     empty with an explanation and a retry — the doctor is never stuck.
 */

import {
  AlertTriangle,
  CheckCircle2,
  FileSignature,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { StatusBadge } from "@/components/common/status-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { CopilotController } from "@/hooks/doctor/use-copilot";
import { cn } from "@/lib/utils";
import type { PrescribedMedication, PrescribedTest, PrescriptionStatus } from "@/types/copilot";

import { AiBadge } from "./copilot-primitives";
import { ConsultationSummaryCard } from "./consultation-summary";

const STAGES: Array<{ status: PrescriptionStatus; label: string }> = [
  { status: "draft", label: "Draft" },
  { status: "doctor_review", label: "Your review" },
  { status: "edited", label: "Edited" },
  { status: "approved", label: "Signed" },
  { status: "issued", label: "Issued" },
];

const emptyMedication = (): PrescribedMedication => ({
  name: "",
  dose: null,
  frequency: null,
  duration: null,
  instructions: null,
  source: "doctor",
});

export function PrescriptionReview({
  copilot,
  doctorName,
}: {
  copilot: CopilotController;
  doctorName: string;
}) {
  const { prescription, session } = copilot;

  /* Local draft state — the doctor's edits are saved explicitly, not per keystroke. */
  const [diagnosis, setDiagnosis] = useState("");
  const [tests, setTests] = useState<PrescribedTest[]>([]);
  const [medications, setMedications] = useState<PrescribedMedication[]>([]);
  const [advice, setAdvice] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState<null | "save" | "sign" | "issue">(null);
  const [error, setError] = useState<string | null>(null);

  // Hydrate from the server draft whenever a new version arrives. Keyed on
  // version so a save that returns v2 refreshes the form, while typing between
  // saves is never clobbered by a poll.
  const version = prescription?.version;
  useEffect(() => {
    if (!prescription) return;
    setDiagnosis(prescription.diagnosis ?? "");
    setTests(prescription.tests);
    setMedications(prescription.medications);
    setAdvice(prescription.advice ?? "");
    setFollowUp(prescription.followUp ?? "");
  }, [prescription?.id, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const stageIndex = useMemo(
    () => STAGES.findIndex((stage) => stage.status === prescription?.status),
    [prescription?.status],
  );

  if (session?.status === "processing") {
    return (
      <Panel>
        <Loader2 className="mx-auto size-5 animate-spin text-primary" aria-hidden />
        <p className="mt-3 text-sm text-muted-foreground">
          Finalising the consultation and preparing your prescription draft…
        </p>
      </Panel>
    );
  }

  if (!prescription) {
    return (
      <Panel>
        <p className="font-display text-base font-semibold">Write the prescription</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {session?.status === "failed"
            ? "The AI could not prepare a draft. You can write the prescription yourself, or try again."
            : "Start a prescription for this consultation."}
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button type="button" onClick={() => void copilot.startPrescription()}>
            Start prescription
          </Button>
          {session?.status === "failed" && (
            <Button type="button" variant="outline" className="gap-2" onClick={copilot.retry}>
              <RefreshCw className="size-4" aria-hidden /> Retry AI
            </Button>
          )}
        </div>
      </Panel>
    );
  }

  const issued = prescription.status === "issued";
  const signed = prescription.status === "approved";
  const editable = !issued;

  const save = async () => {
    setBusy("save");
    setError(null);
    try {
      await copilot.updatePrescription({
        diagnosis,
        tests,
        medications: medications.filter((entry) => entry.name.trim()),
        advice,
        followUp,
      });
    } finally {
      setBusy(null);
    }
  };

  const sign = async () => {
    setBusy("sign");
    setError(null);
    try {
      // Save first: signing content the server has not seen would sign the
      // wrong thing, and the service's hash check would then refuse to issue it.
      await copilot.updatePrescription({
        diagnosis,
        tests,
        medications: medications.filter((entry) => entry.name.trim()),
        advice,
        followUp,
      });
      await copilot.approvePrescription({ signature, doctorName });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The prescription could not be signed.");
    } finally {
      setBusy(null);
    }
  };

  const issue = async () => {
    setBusy("issue");
    setError(null);
    try {
      await copilot.issuePrescription();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The prescription could not be issued.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card-surface overflow-hidden">
        <header className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg font-semibold">Prescription</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {prescription.source === "ai_draft"
                ? "Drafted from the consultation for your review"
                : "Written by you"}{" "}
              · version {prescription.version}
            </p>
          </div>
          <StageRail index={stageIndex} />
        </header>

        {issued ? (
          <IssuedNotice copilot={copilot} />
        ) : (
          <div className="space-y-5 px-5 py-5">
            {prescription.consideredDiagnoses.length > 0 && (
              <ConsideredDiagnoses prescription={prescription} />
            )}

            <Field
              label="Final diagnosis"
              hint="Yours to set. The AI never fills this in."
              required
            >
              <Input
                value={diagnosis}
                onChange={(event) => setDiagnosis(event.target.value)}
                placeholder="e.g. Acute bronchitis"
                disabled={!editable}
              />
            </Field>

            <TestsEditor tests={tests} onChange={setTests} disabled={!editable} />
            <MedicationsEditor
              medications={medications}
              onChange={setMedications}
              disabled={!editable}
            />

            <Field label="Advice">
              <Textarea
                value={advice}
                onChange={(event) => setAdvice(event.target.value)}
                placeholder="Lifestyle advice, precautions, what to watch for"
                rows={3}
                disabled={!editable}
              />
            </Field>

            <Field label="Follow-up">
              <Input
                value={followUp}
                onChange={(event) => setFollowUp(event.target.value)}
                placeholder="e.g. Review in 5 days if symptoms persist"
                disabled={!editable}
              />
            </Field>

            {error && (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-xs leading-relaxed text-destructive"
              >
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                {error}
              </p>
            )}

            <div className="border-t border-border pt-4">
              {signed ? (
                <SignedActions
                  prescription={prescription}
                  busy={busy}
                  onIssue={() => void issue()}
                />
              ) : (
                <SignBlock
                  signature={signature}
                  onSignature={setSignature}
                  doctorName={doctorName}
                  busy={busy}
                  canSign={Boolean(diagnosis.trim() && signature.trim())}
                  onSave={() => void save()}
                  onSign={() => void sign()}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {copilot.summary && <ConsultationSummaryCard summary={copilot.summary} />}
    </div>
  );
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="card-surface grid place-items-center px-6 py-12 text-center" role="status">
      <div className="max-w-sm">{children}</div>
    </div>
  );
}

function StageRail({ index }: { index: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-1.5" aria-label="Prescription progress">
      {STAGES.map((stage, position) => {
        const done = index >= position && index !== -1;
        const current = index === position;
        return (
          <li key={stage.status} className="flex items-center gap-1.5">
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[0.65rem] font-semibold whitespace-nowrap",
                current
                  ? "bg-primary text-primary-foreground"
                  : done
                    ? "bg-success/12 text-success"
                    : "bg-muted text-muted-foreground",
              )}
              aria-current={current ? "step" : undefined}
            >
              {stage.label}
            </span>
            {position < STAGES.length - 1 && <span className="h-px w-2 bg-border" aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string | undefined;
  required?: boolean | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </Label>
      {hint && <p className="text-[0.7rem] text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

function ConsideredDiagnoses({
  prescription,
}: {
  prescription: NonNullable<CopilotController["prescription"]>;
}) {
  return (
    <div className="rounded-lg bg-info/8 px-3 py-2.5">
      <AiBadge className="mb-1.5" />
      <p className="text-[0.7rem] font-semibold">Differentials raised during the consultation</p>
      <ul className="mt-1 space-y-0.5">
        {prescription.consideredDiagnoses.map((entry) => (
          <li key={entry.name} className="text-[0.7rem] leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">{entry.name}</span>
            {entry.rationale ? ` — ${entry.rationale}` : ""}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-[0.65rem] text-muted-foreground">
        For reference only. Type your own diagnosis below.
      </p>
    </div>
  );
}

function TestsEditor({
  tests,
  onChange,
  disabled,
}: {
  tests: PrescribedTest[];
  onChange: (next: PrescribedTest[]) => void;
  disabled: boolean;
}) {
  return (
    <Field label="Diagnostic tests">
      <div className="space-y-1.5">
        {tests.length === 0 && (
          <p className="text-[0.7rem] text-muted-foreground">No tests on this prescription.</p>
        )}
        {tests.map((test, index) => (
          /*
           * Keyed by position, NOT by name.
           *
           * `${test.name}-${index}` looks more unique but is a keystroke bug:
           * the name is what the input edits, so every character changed the
           * key, React unmounted the input and mounted a fresh one, and the
           * field lost focus after each letter.
           */
          <div key={index} className="flex items-center gap-2">
            <Input
              value={test.name}
              onChange={(event) => {
                const next = [...tests];
                next[index] = { ...test, name: event.target.value };
                onChange(next);
              }}
              className="h-8 text-xs"
              disabled={disabled}
              aria-label={`Test ${index + 1}`}
            />
            {test.origin === "ai" && <AiBadge />}
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-destructive"
                onClick={() => onChange(tests.filter((_, position) => position !== index))}
                aria-label={`Remove ${test.name || "test"}`}
              >
                <Trash2 className="size-3.5" aria-hidden />
              </Button>
            )}
          </div>
        ))}
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            onClick={() =>
              onChange([...tests, { name: "", category: null, rationale: null, origin: "doctor" }])
            }
          >
            <Plus className="size-3.5" aria-hidden /> Add a test
          </Button>
        )}
      </div>
    </Field>
  );
}

/**
 * The medications editor.
 *
 * This is the only place in the product where a medication can be written, and
 * the note is not decoration — it is the visible half of a rule the service
 * enforces by rejecting any medication that did not come from a doctor.
 */
function MedicationsEditor({
  medications,
  onChange,
  disabled,
}: {
  medications: PrescribedMedication[];
  onChange: (next: PrescribedMedication[]) => void;
  disabled: boolean;
}) {
  const update = (index: number, patch: Partial<PrescribedMedication>) => {
    const next = [...medications];
    next[index] = { ...next[index]!, ...patch };
    onChange(next);
  };

  return (
    <Field label="Medications" hint="Doctor-entered only. The AI does not suggest medications.">
      <div className="space-y-2">
        {medications.length === 0 && (
          <p className="text-[0.7rem] text-muted-foreground">
            No medications on this prescription.
          </p>
        )}
        {medications.map((medication, index) => (
          <div key={index} className="space-y-1.5 rounded-lg border border-border bg-card p-2.5">
            <div className="flex items-center gap-2">
              <Input
                value={medication.name}
                onChange={(event) => update(index, { name: event.target.value })}
                placeholder="Medication"
                className="h-8 text-xs"
                disabled={disabled}
                aria-label={`Medication ${index + 1} name`}
              />
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0 text-destructive"
                  onClick={() => onChange(medications.filter((_, position) => position !== index))}
                  aria-label={`Remove ${medication.name || "medication"}`}
                >
                  <Trash2 className="size-3.5" aria-hidden />
                </Button>
              )}
            </div>
            <div className="grid gap-1.5 sm:grid-cols-3">
              <Input
                value={medication.dose ?? ""}
                onChange={(event) => update(index, { dose: event.target.value })}
                placeholder="Dose"
                className="h-8 text-xs"
                disabled={disabled}
                aria-label={`Medication ${index + 1} dose`}
              />
              <Input
                value={medication.frequency ?? ""}
                onChange={(event) => update(index, { frequency: event.target.value })}
                placeholder="Frequency"
                className="h-8 text-xs"
                disabled={disabled}
                aria-label={`Medication ${index + 1} frequency`}
              />
              <Input
                value={medication.duration ?? ""}
                onChange={(event) => update(index, { duration: event.target.value })}
                placeholder="Duration"
                className="h-8 text-xs"
                disabled={disabled}
                aria-label={`Medication ${index + 1} duration`}
              />
            </div>
            <Input
              value={medication.instructions ?? ""}
              onChange={(event) => update(index, { instructions: event.target.value })}
              placeholder="Instructions for the patient"
              className="h-8 text-xs"
              disabled={disabled}
              aria-label={`Medication ${index + 1} instructions`}
            />
          </div>
        ))}
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => onChange([...medications, emptyMedication()])}
          >
            <Plus className="size-3.5" aria-hidden /> Add a medication
          </Button>
        )}
      </div>
    </Field>
  );
}

function SignBlock({
  signature,
  onSignature,
  doctorName,
  busy,
  canSign,
  onSave,
  onSign,
}: {
  signature: string;
  onSignature: (value: string) => void;
  doctorName: string;
  busy: string | null;
  canSign: boolean;
  onSave: () => void;
  onSign: () => void;
}) {
  return (
    <div className="space-y-3">
      <Field
        label="Signature"
        hint={`Type your name to sign as ${doctorName}. Signing does not send anything to the patient.`}
        required
      >
        <Input
          value={signature}
          onChange={(event) => onSignature(event.target.value)}
          placeholder={doctorName}
          autoComplete="off"
        />
      </Field>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={onSave} disabled={busy !== null}>
          {busy === "save" ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Save draft
        </Button>
        <Button
          type="button"
          className="gap-2"
          onClick={onSign}
          disabled={!canSign || busy !== null}
        >
          {busy === "sign" ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <FileSignature className="size-4" aria-hidden />
          )}
          Approve &amp; sign
        </Button>
      </div>
      {!canSign && (
        <p className="text-[0.7rem] text-muted-foreground">
          A final diagnosis and your signature are both required before you can approve.
        </p>
      )}
    </div>
  );
}

function SignedActions({
  prescription,
  busy,
  onIssue,
}: {
  prescription: NonNullable<CopilotController["prescription"]>;
  busy: string | null;
  onIssue: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-lg bg-success/8 px-3 py-2.5">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
        <div className="text-xs leading-relaxed">
          <p className="font-semibold">Signed by {prescription.approval?.doctorName ?? "you"}</p>
          <p className="text-muted-foreground">
            {prescription.approval?.approvedAt
              ? new Date(prescription.approval.approvedAt).toLocaleString()
              : ""}
            . The patient has not received it yet.
          </p>
        </div>
      </div>

      <Button type="button" className="gap-2" onClick={onIssue} disabled={busy !== null}>
        {busy === "issue" ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Send className="size-4" aria-hidden />
        )}
        Issue to patient
      </Button>
      <p className="text-[0.7rem] text-muted-foreground">
        Editing anything above revokes this signature and you will need to sign again.
      </p>
    </div>
  );
}

/** §11 of the frontend spec: what the doctor is told after issuance. */
function IssuedNotice({ copilot }: { copilot: CopilotController }) {
  const { prescription, notification } = copilot;
  if (!prescription) return null;

  return (
    <div className="space-y-4 px-5 py-5">
      <div className="flex items-start gap-3 rounded-lg bg-success/8 px-4 py-3">
        <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
        <div>
          <p className="font-display text-sm font-semibold">Prescription issued</p>
          <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3 text-success" aria-hidden />
              Issued {prescription.issuedAt ? new Date(prescription.issuedAt).toLocaleString() : ""}
            </li>
            <li className="flex items-center gap-1.5">
              {notification && notification.status !== "failed" ? (
                <>
                  <CheckCircle2 className="size-3 text-success" aria-hidden />
                  Patient notified
                </>
              ) : (
                <>
                  <AlertTriangle className="size-3 text-warning" aria-hidden />
                  {notification?.failureReason ?? "Patient notification is pending"}
                </>
              )}
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3 text-success" aria-hidden />
              Available in the patient app
            </li>
          </ul>
        </div>
      </div>

      <div className="space-y-2">
        <StatusBadge label="Issued" tone="success" />
        <IssuedSummaryTable prescription={prescription} />
      </div>
    </div>
  );
}

function IssuedSummaryTable({
  prescription,
}: {
  prescription: NonNullable<CopilotController["prescription"]>;
}) {
  const rows: Array<[string, React.ReactNode]> = [
    ["Diagnosis", prescription.diagnosis ?? "—"],
    ["Tests", prescription.tests.map((test) => test.name).join(", ") || "—"],
    [
      "Medications",
      prescription.medications.length === 0
        ? "—"
        : prescription.medications
            .map((entry) =>
              [entry.name, entry.dose, entry.frequency, entry.duration].filter(Boolean).join(" · "),
            )
            .join("\n"),
    ],
    ["Advice", prescription.advice ?? "—"],
    ["Follow-up", prescription.followUp ?? "—"],
  ];

  return (
    <dl className="divide-y divide-border rounded-lg border border-border">
      {rows.map(([label, value]) => (
        <div key={label} className="grid gap-1 px-3 py-2 sm:grid-cols-[9rem_1fr]">
          <dt className="text-xs font-semibold text-muted-foreground">{label}</dt>
          <dd className="text-xs leading-relaxed whitespace-pre-line">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
